import { useMemo } from 'react'
import { Building2, Handshake, Map as MapIcon, Users } from 'lucide-react'
import BSDCard from './BSDCard'
import AutoGrid from './AutoGrid'
import DistrictMap from './DistrictMap'
import { AMBITI_STATS, TOTALI_BACINO, type AmbitoId } from '../data/ambitiStats'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, AreaChart, Area, CartesianGrid, Legend } from 'recharts'
import CompareAmbitiBarCard from './shared/CompareAmbitiBarCard'
import {
  AMBITO_COMPARE_COLORS,
  AMBITO_LABELS,
  CHART_TOOLTIP_STYLE,
  formatInt,
  formatDecimal,
  formatDecimalWithThousands,
  computeRelativeChange,
  computeDeltaPoints,
} from '../utils/dashboardFormatters'

// ── Series storiche ──────────────────────────────────────────────────────────

const INDICE_VECCHIAIA_SERIES_BY_AMBITO: Record<AmbitoId, Array<{ anno: string; valore: number }>> = {
  carate: [
    { anno: '2003', valore: 121.1 },
    { anno: '2007', valore: 128.7 },
    { anno: '2011', valore: 130.6 },
    { anno: '2015', valore: 142.3 },
    { anno: '2019', valore: 158.0 },
    { anno: '2025', valore: 196.1 },
  ],
  desio: [
    { anno: '2003', valore: 112.4 },
    { anno: '2007', valore: 122.3 },
    { anno: '2011', valore: 125.5 },
    { anno: '2015', valore: 137.1 },
    { anno: '2019', valore: 151.4 },
    { anno: '2025', valore: 181.3 },
  ],
  monza: [
    { anno: '2003', valore: 140.7 },
    { anno: '2007', valore: 154.8 },
    { anno: '2011', valore: 159.0 },
    { anno: '2015', valore: 172.1 },
    { anno: '2019', valore: 182.8 },
    { anno: '2025', valore: 207.5 },
  ],
  seregno: [
    { anno: '2003', valore: 126.1 },
    { anno: '2007', valore: 134.9 },
    { anno: '2011', valore: 133.6 },
    { anno: '2015', valore: 142.1 },
    { anno: '2019', valore: 156.5 },
    { anno: '2025', valore: 191.7 },
  ],
  vimercate: [
    { anno: '2003', valore: 113.0 },
    { anno: '2007', valore: 123.1 },
    { anno: '2011', valore: 124.4 },
    { anno: '2015', valore: 137.0 },
    { anno: '2019', valore: 153.3 },
    { anno: '2025', valore: 186.8 },
  ],
}

const INDICE_VECCHIAIA_SERIES_ASST_BRIANZA: Array<{ anno: string; valore: number }> = [
  { anno: '2003', valore: 119.7 },
  { anno: '2007', valore: 128.6 },
  { anno: '2011', valore: 129.3 },
  { anno: '2015', valore: 140.3 },
  { anno: '2019', valore: 159.9 },
  { anno: '2025', valore: 192.1 },
]

const INDICE_VECCHIAIA_DISTRETTI_2025 = {
  carate: 196.1,
  desio: 181.3,
  monza: 207.5,
  seregno: 191.7,
  vimercate: 186.8,
}

const INDICE_DIPENDENZA_SERIES_BY_AMBITO: Record<AmbitoId, Array<{ anno: string; valore: number }>> = {
  carate: [
    { anno: '2003', valore: 45.0 },
    { anno: '2007', valore: 49.2 },
    { anno: '2011', valore: 51.4 },
    { anno: '2015', valore: 54.0 },
    { anno: '2019', valore: 55.8 },
    { anno: '2025', valore: 56.5 },
  ],
  desio: [
    { anno: '2003', valore: 43.6 },
    { anno: '2007', valore: 48.2 },
    { anno: '2011', valore: 50.0 },
    { anno: '2015', valore: 52.9 },
    { anno: '2019', valore: 55.0 },
    { anno: '2025', valore: 55.8 },
  ],
  monza: [
    { anno: '2003', valore: 46.6 },
    { anno: '2007', valore: 53.5 },
    { anno: '2011', valore: 57.6 },
    { anno: '2015', valore: 60.1 },
    { anno: '2019', valore: 60.5 },
    { anno: '2025', valore: 59.2 },
  ],
  seregno: [
    { anno: '2003', valore: 43.8 },
    { anno: '2007', valore: 47.8 },
    { anno: '2011', valore: 50.3 },
    { anno: '2015', valore: 53.8 },
    { anno: '2019', valore: 56.0 },
    { anno: '2025', valore: 57.2 },
  ],
  vimercate: [
    { anno: '2003', valore: 43.3 },
    { anno: '2007', valore: 47.8 },
    { anno: '2011', valore: 50.3 },
    { anno: '2015', valore: 54.6 },
    { anno: '2019', valore: 56.8 },
    { anno: '2025', valore: 56.4 },
  ],
}

const INDICE_DIPENDENZA_SERIES_ASST_BRIANZA: Array<{ anno: string; valore: number }> = [
  { anno: '2003', valore: 43.7 },
  { anno: '2007', valore: 47.9 },
  { anno: '2011', valore: 50.3 },
  { anno: '2015', valore: 53.8 },
  { anno: '2019', valore: 56.8 },
  { anno: '2025', valore: 57.0 },
]

const INDICE_DIPENDENZA_DISTRETTI_2025 = {
  carate: 56.5,
  desio: 55.8,
  monza: 59.2,
  seregno: 57.2,
  vimercate: 56.4,
}

const QUOTA_UNDER15_SERIES_BY_AMBITO: Record<AmbitoId, Array<{ anno: string; valore: number }>> = {
  carate: [
    { anno: '2003', valore: 14.0 },
    { anno: '2007', valore: 14.4 },
    { anno: '2011', valore: 14.7 },
    { anno: '2015', valore: 14.5 },
    { anno: '2019', valore: 13.9 },
    { anno: '2025', valore: 12.2 },
  ],
  desio: [
    { anno: '2003', valore: 14.3 },
    { anno: '2007', valore: 14.6 },
    { anno: '2011', valore: 14.8 },
    { anno: '2015', valore: 14.6 },
    { anno: '2019', valore: 14.1 },
    { anno: '2025', valore: 12.7 },
  ],
  monza: [
    { anno: '2003', valore: 13.2 },
    { anno: '2007', valore: 13.7 },
    { anno: '2011', valore: 14.1 },
    { anno: '2015', valore: 13.8 },
    { anno: '2019', valore: 13.3 },
    { anno: '2025', valore: 12.1 },
  ],
  seregno: [
    { anno: '2003', valore: 13.5 },
    { anno: '2007', valore: 13.8 },
    { anno: '2011', valore: 14.3 },
    { anno: '2015', valore: 14.4 },
    { anno: '2019', valore: 14.0 },
    { anno: '2025', valore: 12.5 },
  ],
  vimercate: [
    { anno: '2003', valore: 14.2 },
    { anno: '2007', valore: 14.5 },
    { anno: '2011', valore: 14.9 },
    { anno: '2015', valore: 14.9 },
    { anno: '2019', valore: 14.3 },
    { anno: '2025', valore: 12.6 },
  ],
}

const QUOTA_UNDER15_SERIES_ASST_BRIANZA: Array<{ anno: string; valore: number }> = [
  { anno: '2003', valore: 13.9 },
  { anno: '2007', valore: 14.2 },
  { anno: '2011', valore: 14.7 },
  { anno: '2015', valore: 14.6 },
  { anno: '2019', valore: 13.9 },
  { anno: '2025', valore: 12.4 },
]

const QUOTA_UNDER15_DISTRETTI_2025 = {
  carate: 12.2,
  desio: 12.7,
  monza: 12.1,
  seregno: 12.5,
  vimercate: 12.6,
}

const QUOTA_OVER65_SERIES_BY_AMBITO: Record<AmbitoId, Array<{ anno: string; valore: number }>> = {
  carate: [
    { anno: '2003', valore: 17.0 },
    { anno: '2007', valore: 18.6 },
    { anno: '2011', valore: 19.2 },
    { anno: '2015', valore: 20.6 },
    { anno: '2019', valore: 21.9 },
    { anno: '2025', valore: 23.9 },
  ],
  desio: [
    { anno: '2003', valore: 16.1 },
    { anno: '2007', valore: 17.9 },
    { anno: '2011', valore: 18.6 },
    { anno: '2015', valore: 20.0 },
    { anno: '2019', valore: 21.4 },
    { anno: '2025', valore: 23.1 },
  ],
  monza: [
    { anno: '2003', valore: 18.6 },
    { anno: '2007', valore: 21.2 },
    { anno: '2011', valore: 22.4 },
    { anno: '2015', valore: 23.7 },
    { anno: '2019', valore: 24.4 },
    { anno: '2025', valore: 25.1 },
  ],
  seregno: [
    { anno: '2003', valore: 17.0 },
    { anno: '2007', valore: 18.6 },
    { anno: '2011', valore: 19.1 },
    { anno: '2015', valore: 20.5 },
    { anno: '2019', valore: 21.9 },
    { anno: '2025', valore: 23.9 },
  ],
  vimercate: [
    { anno: '2003', valore: 16.0 },
    { anno: '2007', valore: 17.8 },
    { anno: '2011', valore: 18.6 },
    { anno: '2015', valore: 20.4 },
    { anno: '2019', valore: 21.9 },
    { anno: '2025', valore: 23.5 },
  ],
}

const QUOTA_OVER65_SERIES_ASST_BRIANZA: Array<{ anno: string; valore: number }> = [
  { anno: '2003', valore: 16.5 },
  { anno: '2007', valore: 18.2 },
  { anno: '2011', valore: 18.8 },
  { anno: '2015', valore: 20.4 },
  { anno: '2019', valore: 22.3 },
  { anno: '2025', valore: 23.9 },
]

const QUOTA_OVER65_DISTRETTI_2025 = {
  carate: 23.9,
  desio: 23.1,
  monza: 25.1,
  seregno: 23.9,
  vimercate: 23.5,
}

const QUOTA_OVER80_SERIES_BY_AMBITO: Record<AmbitoId, Array<{ anno: string; valore: number }>> = {
  carate: [
    { anno: '2003', valore: 3.5 },
    { anno: '2007', valore: 4.2 },
    { anno: '2011', valore: 5.0 },
    { anno: '2015', valore: 5.7 },
    { anno: '2019', valore: 6.7 },
    { anno: '2025', valore: 7.6 },
  ],
  desio: [
    { anno: '2003', valore: 3.0 },
    { anno: '2007', valore: 3.7 },
    { anno: '2011', valore: 4.5 },
    { anno: '2015', valore: 5.4 },
    { anno: '2019', valore: 6.5 },
    { anno: '2025', valore: 7.3 },
  ],
  monza: [
    { anno: '2003', valore: 3.9 },
    { anno: '2007', valore: 4.9 },
    { anno: '2011', valore: 6.0 },
    { anno: '2015', valore: 6.9 },
    { anno: '2019', valore: 7.9 },
    { anno: '2025', valore: 9.0 },
  ],
  seregno: [
    { anno: '2003', valore: 3.4 },
    { anno: '2007', valore: 4.1 },
    { anno: '2011', valore: 4.9 },
    { anno: '2015', valore: 5.8 },
    { anno: '2019', valore: 6.7 },
    { anno: '2025', valore: 7.5 },
  ],
  vimercate: [
    { anno: '2003', valore: 3.3 },
    { anno: '2007', valore: 4.0 },
    { anno: '2011', valore: 4.6 },
    { anno: '2015', valore: 5.3 },
    { anno: '2019', valore: 6.3 },
    { anno: '2025', valore: 7.3 },
  ],
}

const QUOTA_OVER80_SERIES_ATS_BRIANZA: Array<{ anno: string; valore: number }> = [
  { anno: '2003', valore: 3.6 },
  { anno: '2007', valore: 4.4 },
  { anno: '2011', valore: 5.1 },
  { anno: '2015', valore: 5.9 },
  { anno: '2019', valore: 6.9 },
  { anno: '2025', valore: 7.9 },
]

const QUOTA_OVER80_SERIES_ASST_BRIANZA: Array<{ anno: string; valore: number }> = [
  { anno: '2003', valore: 3.4 },
  { anno: '2007', valore: 4.1 },
  { anno: '2011', valore: 4.8 },
  { anno: '2015', valore: 5.6 },
  { anno: '2019', valore: 6.8 },
  { anno: '2025', valore: 7.7 },
]

const QUOTA_OVER80_DISTRETTI_2025 = {
  carate: 7.6,
  desio: 7.3,
  monza: 9.0,
  seregno: 7.5,
  vimercate: 7.3,
}

const POPOLAZIONE_STRANIERA_BY_AMBITO: Record<AmbitoId, {
  stranieri2025: number
  popolazioneTotale2025: number
  percentuale2024: number
  percentuale2025: number
}> = {
  carate: {
    stranieri2025: 11841,
    popolazioneTotale2025: 154719,
    percentuale2024: 7.7,
    percentuale2025: 7.7,
  },
  desio: {
    stranieri2025: 21447,
    popolazioneTotale2025: 194398,
    percentuale2024: 11.1,
    percentuale2025: 11.0,
  },
  monza: {
    stranieri2025: 19282,
    popolazioneTotale2025: 172794,
    percentuale2024: 11.2,
    percentuale2025: 11.2,
  },
  seregno: {
    stranieri2025: 13310,
    popolazioneTotale2025: 170585,
    percentuale2024: 7.8,
    percentuale2025: 7.8,
  },
  vimercate: {
    stranieri2025: 17336,
    popolazioneTotale2025: 187256,
    percentuale2024: 9.3,
    percentuale2025: 9.3,
  },
}

const POPOLAZIONE_STRANIERA_TOTAL = {
  label: 'ATS Brianza',
  stranieri2025: 111247,
  popolazioneTotale2025: 1213556,
  percentuale2024: 9.2,
  percentuale2025: 9.2,
}

const POPOLAZIONE_STRANIERA_ASST_BRIANZA = {
  stranieri2025: 83216,
  popolazioneTotale2025: 879752,
  percentuale2024: 9.5,
  percentuale2025: 9.5,
}

const INDICE_VECCHIAIA_TOOLTIP = `L'indice di vecchiaia evidenzia il livello di invecchiamento della popolazione tramite il rapporto (moltiplicato per 100) tra la popolazione di 65 anni ed oltre e la popolazione fino a 14 anni di età. Un valore basso dell'indice può indicare una elevata natalità ed una ridotta percentuale delle classi anziane: nell'ATS della Brianza i valori sono più bassi rispetto al valore medio nazionale e minimamente superiori al valore lombardo.`

const INDICE_DIPENDENZA_TOOLTIP = `L'indice di dipendenza strutturale (o carico sociale) esprime il rapporto (moltiplicato per 100) tra la popolazione in età non lavorativa (convenzionalmente fino a 14 anni e da 65 anni e oltre) e la popolazione lavorativa (tra 15 e 64 anni). Questo indice rappresenta il divario tra la popolazione potenzialmente produttiva rispetto a quella non produttiva (bambini e anziani): quanto più l'indice si avvicina a 100, tanto più consistente è la quota di popolazione non lavorativa rispetto a quella lavorativa.`

// ── Componente principale ────────────────────────────────────────────────────

export default function DemograficiSection({
  selectedAmbitoId,
  compareAmbiti,
  onSelectedAmbitoChange,
}: {
  selectedAmbitoId: AmbitoId | null
  compareAmbiti: boolean
  onSelectedAmbitoChange: (ambitoId: AmbitoId | null) => void
}) {

  const data = useMemo(() => {
    const selected = selectedAmbitoId ? AMBITI_STATS.find(a => a.id === selectedAmbitoId) || null : null

    if (selected) {
      return {
        titolo: `Ambito di ${selected.nome} - Dati Demografici`,
        maschi: selected.maschi,
        femmine: selected.femmine,
        popolazione: selected.popolazione,
        km2: selected.km2,
        densitaAbKm2: selected.densitaAbKm2,
        numeroComuni: selected.numeroComuni,
        comuni: selected.comuni,
      }
    }

    const allComuni = AMBITI_STATS.flatMap(a => a.comuni)

    return {
      titolo: 'Provincia di Monza e Brianza - Dati Demografici',
      maschi: TOTALI_BACINO.maschi,
      femmine: TOTALI_BACINO.femmine,
      popolazione: TOTALI_BACINO.popolazione,
      km2: TOTALI_BACINO.km2,
      densitaAbKm2: TOTALI_BACINO.densitaAbKm2,
      numeroComuni: TOTALI_BACINO.numeroComuni,
      comuni: allComuni,
    }
  }, [selectedAmbitoId])

  const indiceVecchiaiaData = useMemo(() => {
    const selected = selectedAmbitoId ? AMBITI_STATS.find(a => a.id === selectedAmbitoId) || null : null
    const serie = selectedAmbitoId
      ? INDICE_VECCHIAIA_SERIES_BY_AMBITO[selectedAmbitoId]
      : INDICE_VECCHIAIA_SERIES_ASST_BRIANZA

    const valore2025 = serie[serie.length - 1]?.valore ?? 0
    const riferimentoLabel = selected ? selected.nome : 'ASST Brianza'

    return {
      riferimentoLabel,
      valore2025,
      serie,
      asstBrianza2025: 192.1,
      riepilogoDistretti2025: `Carate ${formatDecimal(INDICE_VECCHIAIA_DISTRETTI_2025.carate)} · Desio ${formatDecimal(INDICE_VECCHIAIA_DISTRETTI_2025.desio)} · Monza ${formatDecimal(INDICE_VECCHIAIA_DISTRETTI_2025.monza)} · Seregno ${formatDecimal(INDICE_VECCHIAIA_DISTRETTI_2025.seregno)} · Vimercate ${formatDecimal(INDICE_VECCHIAIA_DISTRETTI_2025.vimercate)}`,
    }
  }, [selectedAmbitoId])

  const indiceDipendenzaData = useMemo(() => {
    const selected = selectedAmbitoId ? AMBITI_STATS.find(a => a.id === selectedAmbitoId) || null : null
    const serie = selectedAmbitoId
      ? INDICE_DIPENDENZA_SERIES_BY_AMBITO[selectedAmbitoId]
      : INDICE_DIPENDENZA_SERIES_ASST_BRIANZA

    const valore2025 = serie[serie.length - 1]?.valore ?? 0
    const riferimentoLabel = selected ? selected.nome : 'ASST Brianza'

    return {
      riferimentoLabel,
      valore2025,
      serie,
      asstBrianza2025: 57.0,
      riepilogoDistretti2025: `Carate ${formatDecimal(INDICE_DIPENDENZA_DISTRETTI_2025.carate)} · Desio ${formatDecimal(INDICE_DIPENDENZA_DISTRETTI_2025.desio)} · Monza ${formatDecimal(INDICE_DIPENDENZA_DISTRETTI_2025.monza)} · Seregno ${formatDecimal(INDICE_DIPENDENZA_DISTRETTI_2025.seregno)} · Vimercate ${formatDecimal(INDICE_DIPENDENZA_DISTRETTI_2025.vimercate)}`,
    }
  }, [selectedAmbitoId])

  const quotaUnder15Data = useMemo(() => {
    const selected = selectedAmbitoId ? AMBITI_STATS.find(a => a.id === selectedAmbitoId) || null : null
    const serie = selectedAmbitoId
      ? QUOTA_UNDER15_SERIES_BY_AMBITO[selectedAmbitoId]
      : QUOTA_UNDER15_SERIES_ASST_BRIANZA

    const valore2025 = serie[serie.length - 1]?.valore ?? 0
    const riferimentoLabel = selected ? selected.nome : 'ASST Brianza'

    return {
      riferimentoLabel,
      valore2025,
      serie,
      asstBrianza2025: 12.4,
      riepilogoDistretti2025: `Carate ${formatDecimal(QUOTA_UNDER15_DISTRETTI_2025.carate)} · Desio ${formatDecimal(QUOTA_UNDER15_DISTRETTI_2025.desio)} · Monza ${formatDecimal(QUOTA_UNDER15_DISTRETTI_2025.monza)} · Seregno ${formatDecimal(QUOTA_UNDER15_DISTRETTI_2025.seregno)} · Vimercate ${formatDecimal(QUOTA_UNDER15_DISTRETTI_2025.vimercate)}`,
    }
  }, [selectedAmbitoId])

  const quotaOver65Data = useMemo(() => {
    const selected = selectedAmbitoId ? AMBITI_STATS.find(a => a.id === selectedAmbitoId) || null : null
    const serie = selectedAmbitoId
      ? QUOTA_OVER65_SERIES_BY_AMBITO[selectedAmbitoId]
      : QUOTA_OVER65_SERIES_ASST_BRIANZA

    const valore2025 = serie[serie.length - 1]?.valore ?? 0
    const riferimentoLabel = selected ? selected.nome : 'ASST Brianza'

    return {
      riferimentoLabel,
      valore2025,
      serie,
      asstBrianza2025: 23.9,
      riepilogoDistretti2025: `Carate ${formatDecimal(QUOTA_OVER65_DISTRETTI_2025.carate)} · Desio ${formatDecimal(QUOTA_OVER65_DISTRETTI_2025.desio)} · Monza ${formatDecimal(QUOTA_OVER65_DISTRETTI_2025.monza)} · Seregno ${formatDecimal(QUOTA_OVER65_DISTRETTI_2025.seregno)} · Vimercate ${formatDecimal(QUOTA_OVER65_DISTRETTI_2025.vimercate)}`,
    }
  }, [selectedAmbitoId])

  const popolazioneStranieraData = useMemo(() => {
    const selected = selectedAmbitoId ? AMBITI_STATS.find(a => a.id === selectedAmbitoId) || null : null
    const source = selectedAmbitoId
      ? POPOLAZIONE_STRANIERA_BY_AMBITO[selectedAmbitoId]
      : POPOLAZIONE_STRANIERA_TOTAL

    const confronto = [
      { anno: '2024', valore: source.percentuale2024 },
      { anno: '2025', valore: source.percentuale2025 },
    ]

    return {
      riferimentoLabel: selected ? selected.nome : POPOLAZIONE_STRANIERA_TOTAL.label,
      stranieri2025: source.stranieri2025,
      popolazioneTotale2025: source.popolazioneTotale2025,
      percentuale2024: source.percentuale2024,
      percentuale2025: source.percentuale2025,
      confronto,
      asstBrianzaPercent2025: POPOLAZIONE_STRANIERA_ASST_BRIANZA.percentuale2025,
    }
  }, [selectedAmbitoId])

  const quotaOver80Data = useMemo(() => {
    const selected = selectedAmbitoId ? AMBITI_STATS.find(a => a.id === selectedAmbitoId) || null : null
    const serie = selectedAmbitoId
      ? QUOTA_OVER80_SERIES_BY_AMBITO[selectedAmbitoId]
      : QUOTA_OVER80_SERIES_ATS_BRIANZA

    const valore2025 = serie[serie.length - 1]?.valore ?? 0
    const riferimentoLabel = selected ? selected.nome : 'ATS Brianza'

    return {
      riferimentoLabel,
      valore2025,
      serie,
      asstBrianza2025: QUOTA_OVER80_SERIES_ASST_BRIANZA[QUOTA_OVER80_SERIES_ASST_BRIANZA.length - 1]?.valore ?? 0,
      riepilogoDistretti2025: `Carate ${formatDecimal(QUOTA_OVER80_DISTRETTI_2025.carate)} · Desio ${formatDecimal(QUOTA_OVER80_DISTRETTI_2025.desio)} · Monza ${formatDecimal(QUOTA_OVER80_DISTRETTI_2025.monza)} · Seregno ${formatDecimal(QUOTA_OVER80_DISTRETTI_2025.seregno)} · Vimercate ${formatDecimal(QUOTA_OVER80_DISTRETTI_2025.vimercate)}`,
    }
  }, [selectedAmbitoId])

  const comuniList = useMemo(() => {
    return Array.from(new Set(data.comuni)).sort((a, b) => a.localeCompare(b, 'it'))
  }, [data.comuni])

  const compareAmbitiData = useMemo(() => {
    return AMBITI_STATS.map(ambito => ({
      id: ambito.id,
      name: ambito.nome,
      color: AMBITO_COMPARE_COLORS[ambito.id],
      popolazione: ambito.popolazione,
      superficie: ambito.km2,
      comuni: ambito.numeroComuni,
      densita: ambito.densitaAbKm2,
      caricoSociale: INDICE_DIPENDENZA_DISTRETTI_2025[ambito.id],
      evoluzioneCaricoSociale: computeRelativeChange(INDICE_DIPENDENZA_SERIES_BY_AMBITO[ambito.id]),
      indiceVecchiaia: INDICE_VECCHIAIA_DISTRETTI_2025[ambito.id],
      evoluzioneVecchiaia: computeRelativeChange(INDICE_VECCHIAIA_SERIES_BY_AMBITO[ambito.id]),
      quotaUnder15: QUOTA_UNDER15_DISTRETTI_2025[ambito.id],
      trendUnder15: computeDeltaPoints(QUOTA_UNDER15_SERIES_BY_AMBITO[ambito.id]),
      quotaOver65: QUOTA_OVER65_DISTRETTI_2025[ambito.id],
      trendOver65: computeDeltaPoints(QUOTA_OVER65_SERIES_BY_AMBITO[ambito.id]),
      quotaOver80: QUOTA_OVER80_DISTRETTI_2025[ambito.id],
      trendOver80: computeDeltaPoints(QUOTA_OVER80_SERIES_BY_AMBITO[ambito.id]),
      popolazioneStraniera: POPOLAZIONE_STRANIERA_BY_AMBITO[ambito.id].percentuale2025,
      trendPopolazioneStraniera: POPOLAZIONE_STRANIERA_BY_AMBITO[ambito.id].percentuale2025 - POPOLAZIONE_STRANIERA_BY_AMBITO[ambito.id].percentuale2024,
    }))
  }, [])

  const quotaUnder15MultiAmbitoData = useMemo(() => {
    const years = Array.from(new Set(
      Object.values(QUOTA_UNDER15_SERIES_BY_AMBITO).flatMap(serie => serie.map(point => point.anno))
    )).sort((a, b) => Number(a) - Number(b))

    return years.map(anno => {
      const row: { anno: string } & Partial<Record<AmbitoId, number>> = { anno }
      for (const ambito of AMBITI_STATS) {
        row[ambito.id] = QUOTA_UNDER15_SERIES_BY_AMBITO[ambito.id].find(point => point.anno === anno)?.valore
      }
      return row
    })
  }, [])

  const quotaOver65MultiAmbitoData = useMemo(() => {
    const years = Array.from(new Set(
      Object.values(QUOTA_OVER65_SERIES_BY_AMBITO).flatMap(serie => serie.map(point => point.anno))
    )).sort((a, b) => Number(a) - Number(b))

    return years.map(anno => {
      const row: { anno: string } & Partial<Record<AmbitoId, number>> = { anno }
      for (const ambito of AMBITI_STATS) {
        row[ambito.id] = QUOTA_OVER65_SERIES_BY_AMBITO[ambito.id].find(point => point.anno === anno)?.valore
      }
      return row
    })
  }, [])

  const quotaOver80MultiAmbitoData = useMemo(() => {
    const years = Array.from(new Set(
      Object.values(QUOTA_OVER80_SERIES_BY_AMBITO).flatMap(serie => serie.map(point => point.anno))
    )).sort((a, b) => Number(a) - Number(b))

    return years.map(anno => {
      const row: { anno: string } & Partial<Record<AmbitoId, number>> = { anno }
      for (const ambito of AMBITI_STATS) {
        row[ambito.id] = QUOTA_OVER80_SERIES_BY_AMBITO[ambito.id].find(point => point.anno === anno)?.valore
      }
      return row
    })
  }, [])

  const indiceDipendenzaMultiAmbitoData = useMemo(() => {
    const years = Array.from(new Set(
      Object.values(INDICE_DIPENDENZA_SERIES_BY_AMBITO).flatMap(serie => serie.map(point => point.anno))
    )).sort((a, b) => Number(a) - Number(b))

    return years.map(anno => {
      const row: { anno: string } & Partial<Record<AmbitoId, number>> = { anno }
      for (const ambito of AMBITI_STATS) {
        row[ambito.id] = INDICE_DIPENDENZA_SERIES_BY_AMBITO[ambito.id].find(point => point.anno === anno)?.valore
      }
      return row
    })
  }, [])

  const indiceVecchiaiaMultiAmbitoData = useMemo(() => {
    const years = Array.from(new Set(
      Object.values(INDICE_VECCHIAIA_SERIES_BY_AMBITO).flatMap(serie => serie.map(point => point.anno))
    )).sort((a, b) => Number(a) - Number(b))

    return years.map(anno => {
      const row: { anno: string } & Partial<Record<AmbitoId, number>> = { anno }
      for (const ambito of AMBITI_STATS) {
        row[ambito.id] = INDICE_VECCHIAIA_SERIES_BY_AMBITO[ambito.id].find(point => point.anno === anno)?.valore
      }
      return row
    })
  }, [])

  const popolazioneStranieraMultiAmbitoData = useMemo(() => {
    const years = ['2024', '2025'] as const

    return years.map(anno => {
      const row: { anno: string } & Partial<Record<AmbitoId, number>> = { anno }
      for (const ambito of AMBITI_STATS) {
        const stats = POPOLAZIONE_STRANIERA_BY_AMBITO[ambito.id]
        row[ambito.id] = anno === '2024' ? stats.percentuale2024 : stats.percentuale2025
      }
      return row
    })
  }, [])

  return (
    <>
      <h3 className="telemetry-section-title" style={{ marginBottom: 0 }}>{data.titolo}</h3>

      {!compareAmbiti ? (
        <>

      <div className="telemetry-kpi-grid" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        <BSDCard kind="panel" sezione="Dati demografici" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <p style={{ color: 'var(--text-1)', fontSize: 12, lineHeight: 1.6, margin: 0 }}>
            La <strong>Provincia di Monza e della Brianza</strong>è divisa in <strong>Ambiti Territoriali Sociali</strong> che rappresentano aree omogenee per caratteristiche socio-demografiche. <br />Selezionando un Ambito è possibile visualizzare i dati demografici specifici, confrontarli con quelli complessivi della provincia e analizzare l'evoluzione di indicatori chiave come il carico sociale e l'indice di vecchiaia.
          </p>
        </BSDCard>
        <BSDCard className="telemetry-map-card" kind="kpi" sezione="Dati demografici" style={{ minHeight: 120, padding: 10 }}>
          <DistrictMap
            onSelectDistrict={(id) => onSelectedAmbitoChange(id as AmbitoId)}
            onClearSelection={() => onSelectedAmbitoChange(null)}
            selectedDistrictId={selectedAmbitoId ?? null}
          />
        </BSDCard>
        <BSDCard
          kind="kpi"
          sezione="Dati demografici"
          label="Popolazione"
          value={formatInt(data.popolazione)}
          icon={Users}
          color="#0ea5e9"
          sub={
            <>
              Donne:{' '}
              <span style={{ fontWeight: 'bold' }}>{formatInt(data.femmine)}</span><br></br>
              Uomini:{' '}
              <span style={{ fontWeight: 'bold' }}>{formatInt(data.maschi)}</span><br></br>

            </>
          }
        />
        <BSDCard
          kind="kpi"
          sezione="Dati demografici"
          label="Superficie"
          value={`${formatInt(data.km2)} Km²`}
          icon={MapIcon}
          color="#f59e0b"
        />
        <BSDCard
          kind="kpi"
          sezione="Dati demografici"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
              <div style={{ minWidth: 0 }}>
                <span className="telemetry-kpi-label" style={{ display: 'block', marginBottom: 4 }}>Comuni</span>
                <p className="telemetry-kpi-value" style={{ margin: 0 }}>{formatInt(data.numeroComuni)}</p>
              </div>
            </div>

            <div className="telemetry-kpi-icon" style={{ backgroundColor: '#8b5cf620', flexShrink: 0 }}>
              <Building2 size={20} style={{ color: '#8b5cf6' }} />
            </div>
          </div>

          <span style={{ display: 'block', marginTop: 8, width: 'calc(100% + 8px)', marginRight: -8, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4, color: 'var(--text-2)', fontSize: 13, lineHeight: 1.25 }}>
            {comuniList.join(', ')}
          </span>

          <div className="telemetry-kpi-line" style={{ backgroundColor: '#8b5cf6' }} />
        </BSDCard>
      </div>

      <div className="telemetry-kpi-grid" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        <BSDCard
          kind="kpi"
          sezione="Dati demografici"
          label="Densità abitativa"
          value={`${formatDecimalWithThousands(data.densitaAbKm2)} Ab/Km²`}
          icon={Users}
          color="#0ea5e9"
        />
        <BSDCard
          kind="kpi"
          sezione="Dati demografici"
          label="Carico Sociale"
          value={formatDecimal(indiceDipendenzaData.valore2025)}
          icon={Handshake}
          color="#dc2626"
          sub={INDICE_DIPENDENZA_TOOLTIP}
        />

        <BSDCard kind="panel" sezione="Dati demografici" zoomable>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Evoluzione carico sociale</h3>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={indiceDipendenzaData.serie} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={34} />
              <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value: number | string | undefined) => [formatDecimal(Number(value ?? 0)), 'Indice']} />
              <Line type="monotone" dataKey="valore" stroke="#dc2626" strokeWidth={2.2} dot={{ r: 2.4 }} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </BSDCard>

        <BSDCard
          kind="kpi"
          sezione="Dati demografici"
          label="Indice di vecchiaia"
          value={formatDecimal(indiceVecchiaiaData.valore2025)}
          icon={Users}
          color="#2563eb"
          sub={INDICE_VECCHIAIA_TOOLTIP}
        />

        <BSDCard kind="panel" sezione="Dati demografici" zoomable>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Evoluzione indice di vecchiaia</h3>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={indiceVecchiaiaData.serie} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
              <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value: number | string | undefined) => [formatDecimal(Number(value ?? 0)), 'Indice']} />
              <Line type="monotone" dataKey="valore" stroke="#2563eb" strokeWidth={2.2} dot={{ r: 2.4 }} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </BSDCard>
      </div>

      <div className="telemetry-kpi-grid" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        <BSDCard
          kind="kpi"
          sezione="Dati demografici"
          label="Indice popolazione <15 anni"
          value={formatDecimal(quotaUnder15Data.valore2025) + ' %'}
          icon={Users}
          color="#0f766e"
          sub={"Percentuale di popolazione con meno di 15 anni rispetto al totale."}
        />
        <BSDCard kind="panel" sezione="Dati demografici" zoomable>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Trend indice popolazione &lt;15 anni</h3>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={quotaUnder15Data.serie} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
              <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value: number | string | undefined) => [formatDecimal(Number(value ?? 0)), '%']} />
              <Area type="monotone" dataKey="valore" stroke="#0f766e" fill="#0f766e30" strokeWidth={2.1} />
            </AreaChart>
          </ResponsiveContainer>
        </BSDCard>

        <BSDCard
          kind="kpi"
          sezione="Dati demografici"
          label="Indice popolazione 65+ anni"
          value={formatDecimal(quotaOver65Data.valore2025) + ' %'}
          icon={Users}
          color="#b45309"
          sub={"Percentuale di popolazione con 65 anni o più rispetto al totale."}
        />

        <BSDCard kind="panel" sezione="Dati demografici" zoomable>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Trend indice popolazione 65+ anni</h3>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={quotaOver65Data.serie} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
              <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value: number | string | undefined) => [formatDecimal(Number(value ?? 0)), '%']} />
              <Area type="monotone" dataKey="valore" stroke="#b45309" fill="#b4530930" strokeWidth={2.1} />
            </AreaChart>
          </ResponsiveContainer>
        </BSDCard>
        <BSDCard
          kind="kpi"
          sezione="Dati demografici"
          label="Indice popolazione 80+ anni"
          value={formatDecimal(quotaOver80Data.valore2025) + ' %'}
          icon={Users}
          color="#7c3aed"
          sub={"Percentuale di popolazione con 80 anni o più rispetto al totale."}
        />

      </div>

      <div className="telemetry-kpi-grid" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        <BSDCard kind="panel" sezione="Dati demografici" zoomable>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Trend indice popolazione 80+ anni</h3>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={quotaOver80Data.serie} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
              <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value: number | string | undefined) => [formatDecimal(Number(value ?? 0)), '%']} />
              <Area type="monotone" dataKey="valore" stroke="#7c3aed" fill="#7c3aed30" strokeWidth={2.1} />
            </AreaChart>
          </ResponsiveContainer>
        </BSDCard>
        <BSDCard
          kind="kpi"
          sezione="Dati demografici"
          label="Popolazione straniera residente"
          value={`${formatDecimal(popolazioneStranieraData.percentuale2025)} %`}
          icon={Users}
          color="#166534"
          sub={"Percentuale di popolazione residente con cittadinanza straniera rispetto al totale."}
        />

        <BSDCard kind="panel" sezione="Dati demografici" zoomable>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>% stranieri</h3>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={popolazioneStranieraData.confronto} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
              <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value: number | string | undefined) => [`${formatDecimal(Number(value ?? 0))}%`, 'Quota stranieri']} />
              <Area type="monotone" dataKey="valore" stroke="#166534" fill="#16653430" strokeWidth={2.1} />
            </AreaChart>
          </ResponsiveContainer>
        </BSDCard>
      </div>

        </>
      ) : (
        <>
          <AutoGrid>
            <CompareAmbitiBarCard
              title="Popolazione"
              data={compareAmbitiData.map(item => ({ id: item.id, name: item.name, value: item.popolazione, color: item.color }))}
              formatter={formatInt}
              valueLabel="Residenti"
            />
            <CompareAmbitiBarCard
              title="Superficie"
              data={compareAmbitiData.map(item => ({ id: item.id, name: item.name, value: item.superficie, color: item.color }))}
              formatter={(value) => `${formatInt(value)} km²`}
              valueLabel="Km²"
            />
            <CompareAmbitiBarCard
              title="Comuni"
              data={compareAmbitiData.map(item => ({ id: item.id, name: item.name, value: item.comuni, color: item.color }))}
              formatter={formatInt}
              valueLabel="Numero comuni"
            />
            <CompareAmbitiBarCard
              title="Densità abitativa"
              data={compareAmbitiData.map(item => ({ id: item.id, name: item.name, value: item.densita, color: item.color }))}
              formatter={(value) => `${formatDecimalWithThousands(value)} ab/km²`}
              valueLabel="Ab/Km²"
            />
            <CompareAmbitiBarCard
              title="Popolazione straniera"
              data={compareAmbitiData.map(item => ({ id: item.id, name: item.name, value: item.popolazioneStraniera, color: item.color }))}
              formatter={(value) => `${formatDecimal(value)} %`}
              valueLabel="% stranieri"
              sezione="Dati demografici"
            />
          </AutoGrid>


      

          <AutoGrid>
            <CompareAmbitiBarCard
              title="Carico Sociale"
              data={compareAmbitiData.map(item => ({ id: item.id, name: item.name, value: item.caricoSociale, color: item.color }))}
              formatter={formatDecimal}
              valueLabel="Indice"
            />
            <BSDCard kind="panel" sezione="Dati demografici" zoomable>
              <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Evoluzione carico sociale</h3>
              <div className="compare-chart-scroll">
                <div className="compare-chart-scroll-inner compare-chart-scroll-inner--line">
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={indiceDipendenzaMultiAmbitoData} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                  <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip
                    {...CHART_TOOLTIP_STYLE}
                    formatter={(value: number | string | undefined, name: string | number | undefined) => [
                      formatDecimal(Number(value ?? 0)),
                      typeof name === 'string' && name in AMBITO_LABELS ? AMBITO_LABELS[name as AmbitoId] : String(name ?? ''),
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="line"
                    formatter={(value: string) => (value in AMBITO_LABELS ? AMBITO_LABELS[value as AmbitoId] : value)}
                  />
                  {AMBITI_STATS.map(ambito => (
                    <Line
                      key={`carico-sociale-compare-${ambito.id}`}
                      type="monotone"
                      dataKey={ambito.id}
                      stroke={AMBITO_COMPARE_COLORS[ambito.id]}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </BSDCard>
            <CompareAmbitiBarCard
              title="Indice di vecchiaia"
              data={compareAmbitiData.map(item => ({ id: item.id, name: item.name, value: item.indiceVecchiaia, color: item.color }))}
              formatter={formatDecimal}
              valueLabel="Indice"
            />
            <BSDCard kind="panel" sezione="Dati demografici" zoomable>
              <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Evoluzione indice di vecchiaia</h3>
              <div className="compare-chart-scroll">
                <div className="compare-chart-scroll-inner compare-chart-scroll-inner--line">
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={indiceVecchiaiaMultiAmbitoData} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                  <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip
                    {...CHART_TOOLTIP_STYLE}
                    formatter={(value: number | string | undefined, name: string | number | undefined) => [
                      formatDecimal(Number(value ?? 0)),
                      typeof name === 'string' && name in AMBITO_LABELS ? AMBITO_LABELS[name as AmbitoId] : String(name ?? ''),
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="line"
                    formatter={(value: string) => (value in AMBITO_LABELS ? AMBITO_LABELS[value as AmbitoId] : value)}
                  />
                  {AMBITI_STATS.map(ambito => (
                    <Line
                      key={`vecchiaia-compare-${ambito.id}`}
                      type="monotone"
                      dataKey={ambito.id}
                      stroke={AMBITO_COMPARE_COLORS[ambito.id]}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </BSDCard>
            <CompareAmbitiBarCard
              title="Indice popolazione <15 anni"
              data={compareAmbitiData.map(item => ({ id: item.id, name: item.name, value: item.quotaUnder15, color: item.color }))}
              formatter={(value) => `${formatDecimal(value)}%`}
              valueLabel="%"
            />
          </AutoGrid>

          <AutoGrid>
            <BSDCard kind="panel" sezione="Dati demografici" zoomable>
              <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Trend indice popolazione &lt;15 anni</h3>
              <div className="compare-chart-scroll">
                <div className="compare-chart-scroll-inner compare-chart-scroll-inner--line">
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={quotaUnder15MultiAmbitoData} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                  <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip
                    {...CHART_TOOLTIP_STYLE}
                    formatter={(value: number | string | undefined, name: string | number | undefined) => [
                      `${formatDecimal(Number(value ?? 0))}%`,
                      typeof name === 'string' && name in AMBITO_LABELS ? AMBITO_LABELS[name as AmbitoId] : String(name ?? ''),
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="line"
                    formatter={(value: string) => (value in AMBITO_LABELS ? AMBITO_LABELS[value as AmbitoId] : value)}
                  />
                  {AMBITI_STATS.map(ambito => (
                    <Line
                      key={`under15-compare-${ambito.id}`}
                      type="monotone"
                      dataKey={ambito.id}
                      stroke={AMBITO_COMPARE_COLORS[ambito.id]}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </BSDCard>
            <CompareAmbitiBarCard
              title="Indice popolazione 65+ anni"
              data={compareAmbitiData.map(item => ({ id: item.id, name: item.name, value: item.quotaOver65, color: item.color }))}
              formatter={(value) => `${formatDecimal(value)}%`}
              valueLabel="%"
            />
            <BSDCard kind="panel" sezione="Dati demografici" zoomable>
              <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Trend indice popolazione 65+ anni</h3>
              <div className="compare-chart-scroll">
                <div className="compare-chart-scroll-inner compare-chart-scroll-inner--line">
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={quotaOver65MultiAmbitoData} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                  <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip
                    {...CHART_TOOLTIP_STYLE}
                    formatter={(value: number | string | undefined, name: string | number | undefined) => [
                      `${formatDecimal(Number(value ?? 0))}%`,
                      typeof name === 'string' && name in AMBITO_LABELS ? AMBITO_LABELS[name as AmbitoId] : String(name ?? ''),
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="line"
                    formatter={(value: string) => (value in AMBITO_LABELS ? AMBITO_LABELS[value as AmbitoId] : value)}
                  />
                  {AMBITI_STATS.map(ambito => (
                    <Line
                      key={`over65-compare-${ambito.id}`}
                      type="monotone"
                      dataKey={ambito.id}
                      stroke={AMBITO_COMPARE_COLORS[ambito.id]}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </BSDCard>
            <CompareAmbitiBarCard
              title="Indice popolazione 80+ anni"
              data={compareAmbitiData.map(item => ({ id: item.id, name: item.name, value: item.quotaOver80, color: item.color }))}
              formatter={(value) => `${formatDecimal(value)}%`}
              valueLabel="%"
            />
            <BSDCard kind="panel" sezione="Dati demografici" zoomable>
              <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Trend indice popolazione 80+ anni</h3>
              <div className="compare-chart-scroll">
                <div className="compare-chart-scroll-inner compare-chart-scroll-inner--line">
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={quotaOver80MultiAmbitoData} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                  <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip
                    {...CHART_TOOLTIP_STYLE}
                    formatter={(value: number | string | undefined, name: string | number | undefined) => [
                      `${formatDecimal(Number(value ?? 0))}%`,
                      typeof name === 'string' && name in AMBITO_LABELS ? AMBITO_LABELS[name as AmbitoId] : String(name ?? ''),
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="line"
                    formatter={(value: string) => (value in AMBITO_LABELS ? AMBITO_LABELS[value as AmbitoId] : value)}
                  />
                  {AMBITI_STATS.map(ambito => (
                    <Line
                      key={`over80-compare-${ambito.id}`}
                      type="monotone"
                      dataKey={ambito.id}
                      stroke={AMBITO_COMPARE_COLORS[ambito.id]}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </BSDCard>
          </AutoGrid>

          <AutoGrid>
            <CompareAmbitiBarCard
              title="Popolazione straniera residente"
              data={compareAmbitiData.map(item => ({ id: item.id, name: item.name, value: item.popolazioneStraniera, color: item.color }))}
              formatter={(value) => `${formatDecimal(value)}%`}
              valueLabel="% stranieri"
            />
            <BSDCard kind="panel" sezione="Dati demografici" zoomable>
              <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>% stranieri</h3>
              <div className="compare-chart-scroll">
                <div className="compare-chart-scroll-inner compare-chart-scroll-inner--line">
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={popolazioneStranieraMultiAmbitoData} margin={{ top: 2, right: 8, left: -10, bottom: 2 }}>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                  <XAxis dataKey="anno" tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip
                    {...CHART_TOOLTIP_STYLE}
                    formatter={(value: number | string | undefined, name: string | number | undefined) => [
                      `${formatDecimal(Number(value ?? 0))}%`,
                      typeof name === 'string' && name in AMBITO_LABELS ? AMBITO_LABELS[name as AmbitoId] : String(name ?? ''),
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="line"
                    formatter={(value: string) => (value in AMBITO_LABELS ? AMBITO_LABELS[value as AmbitoId] : value)}
                  />
                  {AMBITI_STATS.map(ambito => (
                    <Line
                      key={`stranieri-compare-${ambito.id}`}
                      type="monotone"
                      dataKey={ambito.id}
                      stroke={AMBITO_COMPARE_COLORS[ambito.id]}
                      strokeWidth={2}
                      dot={{ r: 2.6 }}
                      connectNulls
                    />
                  ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </BSDCard>
          </AutoGrid>
        </>
      )}


    </>
  )
}
